/**
 * TicketedEventRefundRequest Entity Schemas
 *
 * A refund request is ticketing's record that money is OWED. No money moves
 * through it: this round records the obligation and the school settles it in
 * Stripe directly, so the payments `Refund` model is untouched and this row
 * links to the Payment only by its public id (KTD9, KD5).
 *
 * Two ways a row appears, AND TWO GRAINS:
 *   - `requested` — a buyer with a paid registration raised it, one row per
 *     paid ORDER (R33). Carries `orderId` and no `admissionId`.
 *   - `owed` — an admin cancelled or voided a paid ADMISSION, which records the
 *     obligation without asking (R38). Carries `admissionId`, and `orderId`
 *     only when there was an order behind the seat: a `comp`, `box_office` or
 *     `walk_up` seat can be priced and has no order at all.
 * Both end at `settled` (who and when are recorded, R40) or `declined`.
 *
 * So both links are nullable and exactly the rule "at least one of the two is
 * set" holds. MySQL cannot express that, so the producers own it; this shape
 * checks it at the boundary, which is the last place a grain-less row can be
 * caught before a reader has to guess what it refers to.
 *
 * Per KTD8 every id here is a PUBLIC id, never a BigInt — a BigInt leak throws
 * at `JSON.stringify` rather than at typecheck.
 */

import { z } from 'zod'

/**
 * The four states of KTD9. `requested` and `owed` differ only in who raised the
 * row, and both are outstanding; `settled` and `declined` are terminal.
 */
export const TicketedEventRefundRequestStatusSchema = z.enum(['requested', 'owed', 'settled', 'declined'])
export type TicketedEventRefundRequestStatus = z.infer<typeof TicketedEventRefundRequestStatusSchema>

/**
 * The field shape. Exported only through the two checked schemas below, which
 * both add the "at least one grain" rule — this object on its own would accept
 * a row that names neither.
 */
const TicketedEventRefundRequestFieldsSchema = z.object({
  id: z.string(),
  // WHICH GRAIN THIS ROW IS. `admissionId` alone is an admin-raised row on an
  // order-less paid seat; `admissionId` with `orderId` is an admin-raised row
  // on a seat that was bought; `orderId` alone is the buyer-raised, order-wide
  // request. Never both null — see the header.
  admissionId: z.string().nullable(),
  orderId: z.string().nullable(),
  // The public id of the Payment this would come out of. Nullable: the soft
  // link is set when the Payment is known, and an admin can owe a refund before
  // that. There is no foreign key behind it — the Payment lives in the payments
  // schema and is reached the same way `TicketedEventOrder.paymentPublicId`
  // reaches it.
  paymentPublicId: z.string().nullable(),
  // What is owed, in cents. Snapshotted from the admission's `priceCents` when
  // the row is raised, so a later tier re-price cannot move a settled sum.
  amountCents: z.number().int(),
  status: TicketedEventRefundRequestStatusSchema,
  requestedAt: z.string(),
  // Set together when an admin settles or declines the row. Null while it is
  // outstanding. WHO settled it is deliberately not on this shape: the actor is
  // a `UsersOnSchools` id, and no surface renders it yet.
  settledAt: z.string().nullable(),
  // Admin free text — why it was declined, or how it was settled.
  note: z.string().nullable()
})

/**
 * A row with neither link is unattributable: nothing can say what the money is
 * owed on. Applied to both shapes rather than to one and inherited, because
 * the public shape is built with `.omit`, and a check written on the full
 * schema is not something `.omit` is guaranteed to carry across.
 */
const hasAtLeastOneGrain = (request: { admissionId: string | null; orderId: string | null }): boolean =>
  request.admissionId !== null || request.orderId !== null

const GRAIN_MESSAGE = 'A refund request must name an admission, an order, or both'

export const TicketedEventRefundRequestSchema = TicketedEventRefundRequestFieldsSchema.refine(hasAtLeastOneGrain, {
  message: GRAIN_MESSAGE,
  path: ['admissionId']
})
export type TicketedEventRefundRequest = z.infer<typeof TicketedEventRefundRequestSchema>

/**
 * The same request, minus the two fields that are for admins only, for the
 * payloads read WITHOUT authentication.
 *
 * The registrant-facing order link (`GET /by-slug/:school/orders/:ref`) is a
 * bearer credential: no auth middleware runs on it and whoever holds the link
 * reads the payload. R33 says the buyer can see the STATUS of their request —
 * it does not say they can see the admin's note or the payments-side identifier
 * of the charge. `note` is written by a school admin about the buyer, and
 * `paymentPublicId` addresses a row in another domain; neither belongs on a
 * link that can be forwarded.
 *
 * `packages/shared-types/src/schemas/entities/ticketed-event-admission.ts`
 * draws the same line for the seat itself (it omits `holderEmail`) and explains
 * where that reduction is enforced at runtime and where it is still
 * aspirational. The same caveat applies here: on the registrant-facing order
 * nothing parses the response on the way out yet, so whichever change teaches
 * `get-public-order.ts` to emit refund state owns parsing through the public
 * shape rather than trusting this declaration.
 *
 * Derived from the shared field object with `.omit` rather than retyped, so
 * the two cannot drift apart, then given the same grain check the full shape
 * carries.
 */
export const TicketedEventRefundRequestPublicSchema = TicketedEventRefundRequestFieldsSchema.omit({
  paymentPublicId: true,
  note: true
}).refine(hasAtLeastOneGrain, { message: GRAIN_MESSAGE, path: ['admissionId'] })
export type TicketedEventRefundRequestPublic = z.infer<typeof TicketedEventRefundRequestPublicSchema>
